import { luminance, saturation, } from '@limonade/color-science';
import { CorrectionEstimator } from '@limonade/matching';
import { frameToImageData } from './frame.js';
import { semanticSkinWeight } from './semantic.js';
import { PCEAnalyzer } from './PCEAnalyzer.js';
import { createPerceptualMetric, createWorkingTransforms } from './colorAnalysis.js';
const DEFAULT_WEIGHTS = {
    skin: 40,
    neutrals: 25,
    highlights: 15,
    shadows: 10,
    background: 10,
};
const SAMPLE_LIMIT = 30_000;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
function sampleLimit(input) {
    if (input.options?.sampling?.maxSamples)
        return Math.max(1, input.options.sampling.maxSamples);
    if (input.options?.sampling?.quality === 'realtime')
        return 5_000;
    if (input.options?.sampling?.quality === 'balanced')
        return 14_000;
    return SAMPLE_LIMIT;
}
function requestedColorManagement(input) {
    return Boolean(input.options?.referenceInputColorSpace ||
        input.options?.sourceInputColorSpace ||
        input.options?.workingColorSpace ||
        input.options?.referenceInputTransferFunction ||
        input.options?.sourceInputTransferFunction);
}
function hue(rgb) {
    const maximum = Math.max(rgb.r, rgb.g, rgb.b);
    const minimum = Math.min(rgb.r, rgb.g, rgb.b);
    const delta = maximum - minimum;
    if (delta < 1e-8)
        return 0;
    let sector = maximum === rgb.r
        ? (rgb.g - rgb.b) / delta
        : maximum === rgb.g ? 2 + (rgb.b - rgb.r) / delta : 4 + (rgb.r - rgb.g) / delta;
    return (sector * 60 + 360) % 360;
}
function isSkin(rgb) {
    const angle = hue(rgb);
    const colorSaturation = saturation(rgb);
    return angle >= 5 && angle <= 55 && colorSaturation >= 0.12 && colorSaturation <= 0.75 &&
        rgb.r >= rgb.g * 0.9 && rgb.g >= rgb.b * 0.75;
}
function isGreen(rgb) {
    return rgb.g > 0.22 && rgb.g - Math.max(rgb.r, rgb.b) > 0.1 &&
        rgb.g > rgb.r * 1.25 && rgb.g > rgb.b * 1.12;
}
function readPixel(frame, nx, ny) {
    const x = Math.min(frame.width - 1, Math.floor(nx * frame.width));
    const y = Math.min(frame.height - 1, Math.floor(ny * frame.height));
    const offset = (y * frame.width + x) * 4;
    return {
        r: (frame.data[offset] ?? 0) / 255,
        g: (frame.data[offset + 1] ?? 0) / 255,
        b: (frame.data[offset + 2] ?? 0) / 255,
    };
}
function classifyRegion(reference, source, nx, ny) {
    if (isGreen(reference) || isGreen(source))
        return 'greenscreen';
    if (isSkin(reference) || isSkin(source))
        return 'skin';
    const averageLuma = (luminance(reference) + luminance(source)) / 2;
    if (averageLuma <= 0.16)
        return 'shadows';
    if (averageLuma >= 0.72)
        return 'highlights';
    if (saturation(reference) <= 0.14 || saturation(source) <= 0.14)
        return 'neutrals';
    if (nx <= 0.18 || nx >= 0.82 || ny <= 0.15 || ny >= 0.85)
        return 'background';
    return 'background';
}
function semanticRegionName(label) {
    const normalized = label.trim().toLowerCase().replaceAll(/[\s-]+/g, '_');
    if (['skin', 'skin_tone', 'face', 'faces', 'person_face', 'face_skin', 'body_skin'].includes(normalized))
        return 'skin';
    if (['neutral', 'neutrals', 'neutral_object', 'gray', 'grey', 'white_balance'].includes(normalized))
        return 'neutrals';
    if (['greenscreen', 'green_screen', 'chroma_screen', 'chroma', 'blue_screen'].includes(normalized))
        return 'greenscreen';
    if (['background', 'sky', 'object', 'objects', 'foreground_object'].includes(normalized))
        return 'background';
    if (['shadow', 'shadows'].includes(normalized))
        return 'shadows';
    if (['highlight', 'highlights'].includes(normalized))
        return 'highlights';
    return undefined;
}
function validateSegmentation(result) {
    if (!Number.isInteger(result.width) || !Number.isInteger(result.height) || result.width <= 0 || result.height <= 0) {
        throw new Error('Semantic segmentation dimensions must be positive integers.');
    }
    const expectedLength = result.width * result.height;
    const regions = result.regions.filter((region) => semanticRegionName(region.label) !== undefined &&
        Number.isFinite(region.confidence) &&
        region.confidence > 0 &&
        region.mask instanceof Uint8Array &&
        region.mask.length === expectedLength);
    return { ...result, regions };
}
function semanticRegionAt(result, nx, ny) {
    if (!result)
        return undefined;
    const x = Math.min(result.width - 1, Math.floor(nx * result.width));
    const y = Math.min(result.height - 1, Math.floor(ny * result.height));
    const index = y * result.width + x;
    let best;
    for (const candidate of result.regions) {
        const region = semanticRegionName(candidate.label);
        if (!region)
            continue;
        const maskValue = candidate.mask[index] ?? 0;
        const normalizedMask = maskValue <= 1 ? maskValue : maskValue / 255;
        const score = clamp01(candidate.confidence) * normalizedMask;
        if (score >= 0.35 && (!best || score > best.score))
            best = { region, score };
    }
    return best;
}
function classifySemanticRegion(semantic, classificationReference, classificationSource, nx, ny) {
    const referenceRegion = semanticRegionAt(semantic.reference, nx, ny);
    const sourceRegion = semanticRegionAt(semantic.source, nx, ny);
    const best = !referenceRegion ? sourceRegion
        : !sourceRegion ? referenceRegion
            : referenceRegion.score >= sourceRegion.score ? referenceRegion : sourceRegion;
    if (best?.region === 'background') {
        // A semantic background can still contain neutral, shadow, highlight, or
        // chroma regions. Do not reclassify it as skin just because its hue is orange.
        const luma = (luminance(classificationReference) + luminance(classificationSource)) / 2;
        const region = isGreen(classificationReference) || isGreen(classificationSource) ? 'greenscreen'
            : luma <= 0.16 ? 'shadows' : luma >= 0.72 ? 'highlights'
                : saturation(classificationReference) <= 0.14 || saturation(classificationSource) <= 0.14 ? 'neutrals' : 'background';
        return { region, semantic: true };
    }
    return best
        ? { region: best.region, semantic: true }
        : { region: classifyRegion(classificationReference, classificationSource, nx, ny), semantic: false };
}
function markerRegions(input) {
    const markers = input.analyzerContext?.trackingMarkers?.metrics.markers ?? [];
    const overlays = input.analyzerContext?.trackingMarkers?.overlays ?? [];
    return markers.map((marker) => {
        const overlay = overlays.find((item) => item.type === 'circle' && item.label === marker.id);
        return {
            x: marker.normalized_x,
            y: marker.normalized_y,
            radius: Math.max(overlay?.radius ?? 0.025, 0.012),
        };
    });
}
function insideMarker(nx, ny, markers) {
    return markers.some((marker) => Math.hypot(nx - marker.x, ny - marker.y) <= marker.radius * 1.35);
}
function sourceWithoutMarkers(reference, source, markers) {
    if (!markers.length)
        return source;
    const data = new Uint8ClampedArray(source.data);
    for (let y = 0; y < source.height; y++) {
        const ny = (y + 0.5) / source.height;
        for (let x = 0; x < source.width; x++) {
            const nx = (x + 0.5) / source.width;
            if (!insideMarker(nx, ny, markers))
                continue;
            const referenceX = Math.min(reference.width - 1, Math.floor(nx * reference.width));
            const referenceY = Math.min(reference.height - 1, Math.floor(ny * reference.height));
            const sourceOffset = (y * source.width + x) * 4;
            const referenceOffset = (referenceY * reference.width + referenceX) * 4;
            data[sourceOffset] = reference.data[referenceOffset] ?? 0;
            data[sourceOffset + 1] = reference.data[referenceOffset + 1] ?? 0;
            data[sourceOffset + 2] = reference.data[referenceOffset + 2] ?? 0;
            data[sourceOffset + 3] = source.data[sourceOffset + 3] ?? 255;
        }
    }
    return { width: source.width, height: source.height, data, colorSpace: source.colorSpace };
}
function blendScalar(previous, current, currentWeight) {
    return round(previous * (1 - currentWeight) + current * currentWeight);
}
function blendCorrections(previous, current, currentWeight) {
    const blendArray = (first, second) => second.map((value, index) => blendScalar(first[index] ?? value, value, currentWeight));
    const blendWhitePoint = () => {
        if (!previous.whitePoint)
            return current.whitePoint;
        if (!current.whitePoint)
            return previous.whitePoint;
        const blendSample = (first, second) => ({
            r: blendScalar(first.r, second.r, currentWeight),
            g: blendScalar(first.g, second.g, currentWeight),
            b: blendScalar(first.b, second.b, currentWeight),
            sampleCount: Math.round(first.sampleCount * (1 - currentWeight) + second.sampleCount * currentWeight),
            confidence: blendScalar(first.confidence, second.confidence, currentWeight),
        });
        return {
            reference: blendSample(previous.whitePoint.reference, current.whitePoint.reference),
            source: blendSample(previous.whitePoint.source, current.whitePoint.source),
            channelGains: current.whitePoint.channelGains.map((value, index) => blendScalar(previous.whitePoint?.channelGains[index] ?? value, value, currentWeight)),
            confidence: blendScalar(previous.whitePoint.confidence, current.whitePoint.confidence, currentWeight),
        };
    };
    const blendBlackPoint = () => {
        if (!previous.blackPoint)
            return current.blackPoint;
        if (!current.blackPoint)
            return previous.blackPoint;
        const blendSample = (first, second) => ({
            r: blendScalar(first.r, second.r, currentWeight),
            g: blendScalar(first.g, second.g, currentWeight),
            b: blendScalar(first.b, second.b, currentWeight),
            sampleCount: Math.round(first.sampleCount * (1 - currentWeight) + second.sampleCount * currentWeight),
            confidence: blendScalar(first.confidence, second.confidence, currentWeight),
        });
        return {
            reference: blendSample(previous.blackPoint.reference, current.blackPoint.reference),
            source: blendSample(previous.blackPoint.source, current.blackPoint.source),
            channelOffsets: current.blackPoint.channelOffsets.map((value, index) => blendScalar(previous.blackPoint?.channelOffsets[index] ?? value, value, currentWeight)),
            confidence: blendScalar(previous.blackPoint.confidence, current.blackPoint.confidence, currentWeight),
        };
    };
    const blendSaturationMatch = () => {
        if (!previous.saturationMatch)
            return current.saturationMatch;
        if (!current.saturationMatch)
            return previous.saturationMatch;
        return {
            referenceMean: blendScalar(previous.saturationMatch.referenceMean, current.saturationMatch.referenceMean, currentWeight),
            sourceMean: blendScalar(previous.saturationMatch.sourceMean, current.saturationMatch.sourceMean, currentWeight),
            saturationRatio: blendScalar(previous.saturationMatch.saturationRatio, current.saturationMatch.saturationRatio, currentWeight),
            confidence: blendScalar(previous.saturationMatch.confidence, current.saturationMatch.confidence, currentWeight),
        };
    };
    const blendSkinToneMatch = () => {
        if (!previous.skinToneMatch)
            return current.skinToneMatch;
        if (!current.skinToneMatch)
            return previous.skinToneMatch;
        const blendSample = (first, second) => ({
            r: blendScalar(first.r, second.r, currentWeight),
            g: blendScalar(first.g, second.g, currentWeight),
            b: blendScalar(first.b, second.b, currentWeight),
            sampleCount: Math.round(first.sampleCount * (1 - currentWeight) + second.sampleCount * currentWeight),
            confidence: blendScalar(first.confidence, second.confidence, currentWeight),
        });
        return {
            reference: blendSample(previous.skinToneMatch.reference, current.skinToneMatch.reference),
            source: blendSample(previous.skinToneMatch.source, current.skinToneMatch.source),
            channelPower: current.skinToneMatch.channelPower.map((value, index) => blendScalar(previous.skinToneMatch?.channelPower[index] ?? value, value, currentWeight)),
            confidence: blendScalar(previous.skinToneMatch.confidence, current.skinToneMatch.confidence, currentWeight),
        };
    };
    const blendColorVectorMatch = () => {
        if (!previous.colorVectorMatch)
            return current.colorVectorMatch;
        if (!current.colorVectorMatch)
            return previous.colorVectorMatch;
        return {
            vectors: current.colorVectorMatch.vectors.map((vector, index) => {
                const old = previous.colorVectorMatch?.vectors[index];
                return {
                    ...vector,
                    referenceHue: old?.referenceHue === null || old?.referenceHue === undefined || vector.referenceHue === null
                        ? vector.referenceHue
                        : blendScalar(old.referenceHue, vector.referenceHue, currentWeight),
                    sourceHue: old?.sourceHue === null || old?.sourceHue === undefined || vector.sourceHue === null
                        ? vector.sourceHue
                        : blendScalar(old.sourceHue, vector.sourceHue, currentWeight),
                    hueShiftDegrees: blendScalar(old?.hueShiftDegrees ?? vector.hueShiftDegrees, vector.hueShiftDegrees, currentWeight),
                    referenceSampleCount: Math.round((old?.referenceSampleCount ?? vector.referenceSampleCount) * (1 - currentWeight) + vector.referenceSampleCount * currentWeight),
                    sourceSampleCount: Math.round((old?.sourceSampleCount ?? vector.sourceSampleCount) * (1 - currentWeight) + vector.sourceSampleCount * currentWeight),
                    confidence: blendScalar(old?.confidence ?? vector.confidence, vector.confidence, currentWeight),
                };
            }),
            hueVsHueCurve: current.colorVectorMatch.hueVsHueCurve.map((value, index) => blendScalar(previous.colorVectorMatch?.hueVsHueCurve[index] ?? value, value, currentWeight)),
            confidence: blendScalar(previous.colorVectorMatch.confidence, current.colorVectorMatch.confidence, currentWeight),
        };
    };
    return {
        temperatureShift: blendScalar(previous.temperatureShift, current.temperatureShift, currentWeight),
        tintShift: blendScalar(previous.tintShift, current.tintShift, currentWeight),
        exposureShift: blendScalar(previous.exposureShift, current.exposureShift, currentWeight),
        saturationShift: blendScalar(previous.saturationShift, current.saturationShift, currentWeight),
        contrastShift: blendScalar(previous.contrastShift, current.contrastShift, currentWeight),
        toneCurve: blendArray(previous.toneCurve, current.toneCurve),
        hueVsHue: blendArray(previous.hueVsHue, current.hueVsHue),
        hueVsSat: blendArray(previous.hueVsSat, current.hueVsSat),
        whitePoint: blendWhitePoint(),
        blackPoint: blendBlackPoint(),
        saturationMatch: blendSaturationMatch(),
        skinToneMatch: blendSkinToneMatch(),
        colorVectorMatch: blendColorVectorMatch(),
        tonalCorrection: current.tonalCorrection,
    };
}
/** Scene-aware PCE v2 layer that preserves PCEAnalyzer as the base comparator. */
export class SceneAwarePCEAnalyzer {
    id = 'scene-aware-pce';
    label = 'Scene-Aware Perceptual Color Engine';
    base = new PCEAnalyzer();
    semanticSegmentationProvider;
    previousCorrections;
    previousMatchScore;
    constructor(options = {}) {
        this.semanticSegmentationProvider = options.semanticSegmentationProvider;
    }
    reset() {
        this.base.reset();
        this.previousCorrections = undefined;
        this.previousMatchScore = undefined;
    }
    async analyze(input) {
        if (input.options?.palettePresetId) {
            // A color strip has no photographed skin/face/neutral correspondence.
            // Do not let scene masks or technical QC silently cancel a creative look.
            const result = await this.base.analyze(input);
            return { ...result, regionScores: {}, regionWeights: { ...DEFAULT_WEIGHTS },
                semanticAnalysis: { mode: 'heuristic', skinMatching: 'disabled' },
                warnings: [...result.diagnostics], recommendations: ['Inspect the global palette look and exported CUBE. This is not a spatial skin grade.'] };
        }
        const semanticPromise = this.resolveSemanticContext(input);
        const [referenceFrame, sourceFrame] = await Promise.all([
            frameToImageData(input.referenceFrame),
            frameToImageData(input.sourceFrame),
        ]);
        const semantic = await semanticPromise;
        const markers = markerRegions(input);
        const creativeSourceFrame = sourceWithoutMarkers(referenceFrame, sourceFrame, markers);
        const baseResult = await this.base.analyze({
            referenceFrame,
            sourceFrame: creativeSourceFrame,
            options: { ...input.options, temporalStability: false },
            semanticContext: semantic,
        });
        const { reference: referenceTransform, source: sourceTransform, warnings: regionTransformWarnings } = createWorkingTransforms(input.options, {
            referenceInputColorSpace: baseResult.colorManagement.inputColorSpace.reference,
            sourceInputColorSpace: baseResult.colorManagement.inputColorSpace.source,
            workingColorSpace: baseResult.colorManagement.workingColorSpace,
        });
        const distance = createPerceptualMetric(baseResult.colorManagement.workingColorSpace);
        const weights = {
            ...DEFAULT_WEIGHTS,
            skin: input.options?.protectSkin ? 55 : DEFAULT_WEIGHTS.skin,
        };
        const accumulators = {
            skin: { distance: 0, count: 0 },
            neutrals: { distance: 0, count: 0 },
            shadows: { distance: 0, count: 0 },
            highlights: { distance: 0, count: 0 },
            background: { distance: 0, count: 0 },
            greenscreen: { distance: 0, count: 0 },
        };
        const target = Math.min(sampleLimit(input), Math.max(referenceFrame.width * referenceFrame.height, sourceFrame.width * sourceFrame.height));
        const columns = Math.max(1, Math.round(Math.sqrt(target * referenceFrame.width / Math.max(1, referenceFrame.height))));
        const rows = Math.max(1, Math.ceil(target / columns));
        const rowStride = input.options?.sampling?.stride ? Math.max(1, Math.floor(input.options.sampling.stride)) : 1;
        let excludedMarkers = 0;
        let lowConfidenceBoundarySamples = 0;
        let semanticSamples = 0;
        const referenceSkinSamples = [];
        const sourceSkinSamples = [];
        const edge = input.analyzerContext?.edgeConfidence?.metrics;
        const weakEdgeRatio = edge ? clamp01(edge.weak_edge_percentage / 100) : 0;
        for (let row = 0; row < rows; row += rowStride) {
            for (let column = 0; column < columns; column += rowStride) {
                const nx = (column + 0.5) / columns;
                const ny = (row + 0.5) / rows;
                if (insideMarker(nx, ny, markers)) {
                    excludedMarkers++;
                    continue;
                }
                const reference = readPixel(referenceFrame, nx, ny);
                const source = readPixel(sourceFrame, nx, ny);
                const referenceWorking = referenceTransform?.rgb(reference) ?? reference;
                const sourceWorking = sourceTransform?.rgb(source) ?? source;
                const referenceSkin = semanticSkinWeight(semantic.reference, nx, ny) ?? 0;
                const sourceSkin = semanticSkinWeight(semantic.source, nx, ny) ?? 0;
                if (referenceSkin >= 0.65)
                    referenceSkinSamples.push({ ...referenceWorking, weight: referenceSkin });
                if (sourceSkin >= 0.65)
                    sourceSkinSamples.push({ ...sourceWorking, weight: sourceSkin });
                const classificationReference = referenceTransform ? referenceWorking : reference;
                const classificationSource = sourceTransform ? sourceWorking : source;
                const classification = classifySemanticRegion(semantic, classificationReference, classificationSource, nx, ny);
                const region = classification.region;
                if (classification.semantic)
                    semanticSamples++;
                const accumulator = accumulators[region];
                let confidenceWeight = 1;
                if (weakEdgeRatio > 0 && nx > 0.18 && nx < 0.82 && ny > 0.12 && ny < 0.88) {
                    confidenceWeight = 1 - weakEdgeRatio * 0.45;
                    lowConfidenceBoundarySamples++;
                }
                accumulator.distance += distance(referenceWorking, sourceWorking) * confidenceWeight;
                accumulator.count += confidenceWeight;
            }
        }
        const scoreFor = (region) => {
            if (region === 'skin' && semantic.reference && semantic.source) {
                if (!referenceSkinSamples.length || !sourceSkinSamples.length)
                    return undefined;
                const mean = (samples) => {
                    let r = 0, g = 0, b = 0, weight = 0;
                    for (const sample of samples) {
                        const w = sample.weight ?? 1;
                        r += sample.r * w;
                        g += sample.g * w;
                        b += sample.b * w;
                        weight += w;
                    }
                    return { r: r / weight, g: g / weight, b: b / weight };
                };
                const referenceMean = mean(referenceSkinSamples);
                const sourceMean = mean(sourceSkinSamples);
                return round(Math.exp(-distance(referenceMean, sourceMean) * 4.5));
            }
            const value = accumulators[region];
            return value.count > 0 ? round(Math.exp(-(value.distance / value.count) * 4.5)) : undefined;
        };
        const regionScores = {
            skin: scoreFor('skin'),
            neutrals: scoreFor('neutrals'),
            shadows: scoreFor('shadows'),
            highlights: scoreFor('highlights'),
            background: scoreFor('background'),
            greenscreen: scoreFor('greenscreen'),
        };
        let weightedTotal = 0;
        let activeWeight = 0;
        Object.keys(weights).forEach((region) => {
            const score = regionScores[region];
            if (score === undefined)
                return;
            weightedTotal += score * weights[region];
            activeWeight += weights[region];
        });
        if (!input.options?.ignoreGreenscreen && regionScores.greenscreen !== undefined) {
            weightedTotal += regionScores.greenscreen * weights.background;
            activeWeight += weights.background;
        }
        const weightedRegionScore = activeWeight ? weightedTotal / activeWeight : baseResult.overallMatchScore;
        const overallMatchScore = round(clamp01(baseResult.overallMatchScore * 0.45 + weightedRegionScore * 0.55));
        const warnings = [...(baseResult.tonalAnalysis?.warnings ?? [])];
        const recommendations = [];
        const context = input.analyzerContext;
        if (context?.spill?.metrics.spill_score && context.spill.metrics.spill_score >= 0.18 && regionScores.skin !== undefined) {
            warnings.push('Green spill affecting skin');
            recommendations.push('Reduce spill before applying global skin-tone corrections.');
        }
        if (context?.exposure?.metrics.exposure_status === 'underexposed') {
            warnings.push('Source image underexposed');
            recommendations.push('Restore source exposure before fine hue matching.');
        }
        if ((context?.exposure?.metrics.highlight_clip_percentage ?? 0) >= 1) {
            warnings.push('Highlight clipping detected');
            recommendations.push('Recover or roll off highlights before matching the tone curve.');
        }
        if (excludedMarkers > 0) {
            warnings.push('Tracking markers excluded from match');
            recommendations.push('Keep tracking-marker exclusion enabled for creative color matching.');
        }
        if (weakEdgeRatio >= 0.2) {
            recommendations.push('Treat corrections near matte boundaries conservatively because edge confidence is low.');
        }
        if ((context?.lighting?.metrics.background_uniformity ?? 1) < 0.65) {
            recommendations.push('Correct uneven background lighting before relying on background color scores.');
        }
        const artifactScore = context?.videoArtifacts?.artifact_score ?? 0;
        const artifactRiskPenalty = context?.videoArtifacts?.overall_artifact_risk === 'high'
            ? 0.3 : context?.videoArtifacts?.overall_artifact_risk === 'medium' ? 0.16 : 0;
        const edgePenalty = edge ? (1 - edge.final_edge_confidence_score) * 0.14 : 0;
        let confidence = clamp01(baseResult.confidence * (1 - artifactScore * 0.35 - artifactRiskPenalty - edgePenalty));
        if (artifactScore >= 0.45) {
            recommendations.push('Reduce compression, noise, or temporal artifacts before final color matching.');
        }
        let suggestedCorrections = baseResult.suggestedCorrections;
        let skinMatching = input.options?.matchMidtoneSkin === false ? 'disabled' : 'color-heuristic';
        if (semantic.reference && semantic.source && skinMatching !== 'disabled') {
            if (referenceSkinSamples.length >= 8 && sourceSkinSamples.length >= 8) {
                const skinToneMatch = new CorrectionEstimator().estimateSkinToneMatch(referenceSkinSamples, sourceSkinSamples, true);
                suggestedCorrections = { ...suggestedCorrections, skinToneMatch };
                skinMatching = 'semantic-masks';
            }
            else {
                suggestedCorrections = { ...suggestedCorrections, skinToneMatch: undefined };
                skinMatching = 'insufficient-skin';
                warnings.push('Insufficient confidently detected skin in both images; skin matching was skipped.');
            }
        }
        let temporal;
        if (input.options?.temporalStability) {
            const flow = context?.opticalFlow?.metrics;
            const flickerRisk = clamp01(Math.max(context?.videoArtifacts?.metrics.flicker_score ?? 0, flow ? (1 - flow.motion_stability) * 0.65 + flow.shake_score * 0.35 : 0.45));
            const stabilityScore = clamp01((flow?.motion_stability ?? 0.5) * (1 - flickerRisk * 0.65));
            const motionConfidence = flow?.confidence ?? 0;
            const motionCompensatedMatchScore = round(clamp01(overallMatchScore + (1 - overallMatchScore) * stabilityScore * motionConfidence * 0.08));
            temporal = {
                stabilityScore: round(stabilityScore),
                flickerRisk: round(flickerRisk),
                motionCompensatedMatchScore,
            };
            if (this.previousCorrections) {
                const currentWeight = clamp01(0.18 + stabilityScore * 0.52);
                suggestedCorrections = blendCorrections(this.previousCorrections, suggestedCorrections, currentWeight);
            }
            if (flickerRisk >= 0.35 || stabilityScore < 0.55) {
                warnings.push('Temporal instability may cause correction flicker');
                recommendations.push('Use temporally smoothed corrections until motion and flicker stabilize.');
                confidence = clamp01(confidence * (1 - flickerRisk * 0.15));
            }
            if (this.previousMatchScore !== undefined && Math.abs(overallMatchScore - this.previousMatchScore) > 0.18) {
                recommendations.push('Review the abrupt match-score change before committing an automated correction.');
            }
        }
        if (skinMatching === 'disabled' || skinMatching === 'insufficient-skin') {
            suggestedCorrections = { ...suggestedCorrections, skinToneMatch: undefined };
        }
        if (input.options?.matchSixVectorColors === false) {
            suggestedCorrections = { ...suggestedCorrections, colorVectorMatch: undefined, hueVsHue: Array(12).fill(0), hueVsSat: Array(12).fill(0), tonalCorrection: undefined };
        }
        this.previousCorrections = suggestedCorrections;
        this.previousMatchScore = overallMatchScore;
        return {
            ...baseResult,
            overallMatchScore,
            confidence: round(confidence),
            suggestedCorrections,
            semanticAnalysis: {
                mode: semantic.reference && semantic.source ? 'semantic' : semantic.reference || semantic.source ? 'partial' : 'heuristic',
                reference: semantic.reference?.detection,
                source: semantic.source?.detection,
                skinMatching,
            },
            diagnostics: [
                ...baseResult.diagnostics,
                `Computed scene-aware scores from ${columns * rows - excludedMarkers} unmasked samples.`,
                baseResult.colorManagement.workingColorSpace !== 'OKLab-analysis-from-sRGB' || referenceTransform || sourceTransform
                    ? `Scene-aware region scoring used ${baseResult.colorManagement.workingColorSpace} working-space samples.`
                    : requestedColorManagement(input) && baseResult.colorManagement.transformWarnings.length > 0
                        ? 'Scene-aware region scoring fell back to raw sRGB heuristics because no supported working color space was active.'
                        : 'Scene-aware region scoring used deterministic sRGB/OKLab-compatible heuristics.',
                'Perceptual color distance uses OKLab; RGB working spaces are not scored with raw RGB distance.',
                ...regionTransformWarnings,
                this.semanticSegmentationProvider || input.semanticContext
                    ? semantic.failures > 0
                        ? `Semantic segmentation failed for ${semantic.failures} frame(s); deterministic region heuristics were used as fallback.`
                        : semantic.reference || semantic.source
                            ? `Semantic segmentation contributed to ${semanticSamples} region samples.`
                            : 'Semantic masks were unavailable; deterministic region heuristics were used.'
                    : 'No semantic segmentation provider was configured; deterministic region heuristics were used.',
                input.options?.ignoreGreenscreen
                    ? 'Green-screen samples were excluded from the creative weighted score.'
                    : 'Green-screen samples were included in the creative weighted score.',
                `${lowConfidenceBoundarySamples} samples received reduced matte-boundary confidence.`,
            ],
            regionScores,
            regionWeights: weights,
            temporal,
            warnings,
            recommendations,
        };
    }
    async resolveSemanticContext(input) {
        if (input.semanticContext) {
            const context = { failures: 0 };
            for (const key of ['reference', 'source']) {
                if (!input.semanticContext[key])
                    continue;
                try {
                    context[key] = validateSegmentation(input.semanticContext[key]);
                }
                catch {
                    context.failures++;
                }
            }
            return context;
        }
        if (!this.semanticSegmentationProvider)
            return { failures: 0 };
        const results = await Promise.allSettled([
            this.semanticSegmentationProvider.segment(input.referenceFrame),
            this.semanticSegmentationProvider.segment(input.sourceFrame),
        ]);
        const semantic = { failures: 0 };
        const targets = ['reference', 'source'];
        results.forEach((result, index) => {
            if (result.status === 'fulfilled') {
                try {
                    semantic[targets[index]] = validateSegmentation(result.value);
                }
                catch {
                    semantic.failures++;
                }
            }
            else {
                semantic.failures++;
            }
        });
        return semantic;
    }
}
//# sourceMappingURL=SceneAwarePCEAnalyzer.js.map